/**
 * The Code of Conduct catalogue's shared vocabulary — the level→article-range grouping §14
 * draws its optgroups from, and the two label shapes that spec prints verbatim.
 *
 * Authority: `docs/specs/control-centre/06-hearings.md` §7 (the three levels, their article
 * ranges and the minimum penalty each carries), §14 (the create modal's optgroups
 * `Level One — Article 2.2` / `Level Two — Article 2.3` / `Level Three — Article 2.4`, and the
 * option text `2.2.a — {first 74 chars}…`), §2 (versioning is mandatory), and migration
 * `028-code-of-conduct-catalogue.sql` (the table these labels group).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT MAY LIVE IN CODE HERE, AND WHAT MAY NOT — THE §2 LINE, DRAWN ONCE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §2 is unambiguous: the offence catalogue binds to the specific Code edition in force and is
 * *"never a hard-coded list"*. Nothing in this file is a list of offences. What is here is the
 * ladder those offences hang on, which is the part that does not shift between editions:
 *
 *  * **The three levels and their minimum penalties** already live in `hearings.ts` as
 *    {@link HEARING_LEVEL_MINIMUM_PENALTY}, with the reasoning stated there. Not restated.
 *  * **The article RANGE per level** — `2.2` / `2.3` / `2.4` — is here as a FALLBACK and
 *    nothing more. The live value is `penalty_ladder.article_range` (migration 002), which is
 *    tournament data; this constant is what a level group is labelled with when a tournament's
 *    ladder has not been seeded, exactly as `DEFAULT_ENGINE_CONFIG` stands in for thresholds
 *    nobody has typed. A screen that renders `Level One` with no range is a screen that has
 *    lost §14's heading; a screen that renders a range nobody configured, without saying so,
 *    would be worse.
 *  * **The lettering inside a range** — `a`, `b`, … `m` — is exactly what may NOT live here,
 *    and does not. It is loaded, per edition, into `code_of_conduct_article`.
 *
 * British English throughout, per the standing copy rule.
 */
/** The three levels, in ladder order. */
export const HEARING_LEVELS = [1, 2, 3];
/**
 * §7's article range per level, as a fallback for a tournament whose `penalty_ladder` has not
 * been seeded. See the module note: the RANGE is the rung, the lettering inside it is the
 * catalogue, and only the second of those is forbidden to code.
 */
export const HEARING_LEVEL_ARTICLE_RANGE = {
    1: "2.2",
    2: "2.3",
    3: "2.4",
};
/** §14 spells the level out in words in its optgroup headings, and nowhere else. */
export const HEARING_LEVEL_WORD = {
    1: "One",
    2: "Two",
    3: "Three",
};
/**
 * §14's optgroup heading: `Level One — Article 2.2`.
 *
 * `articleRange` is the tournament's own `penalty_ladder.article_range` where it has one, and
 * `null` where it has not — in which case the fallback above supplies it. Passing the range in
 * rather than reading the constant directly is what keeps this function honest when a future
 * edition renumbers: the heading follows the data, and the constant is only ever the answer to
 * a question nobody has configured.
 */
export function codeOfConductLevelLabel(level, articleRange = null) {
    const range = articleRange ?? HEARING_LEVEL_ARTICLE_RANGE[level];
    return `Level ${HEARING_LEVEL_WORD[level]} — Article ${range}`;
}
/** §14's option text truncates the wording at 74 characters and appends an ellipsis. */
export const ARTICLE_OPTION_TEXT_LIMIT = 74;
/**
 * §14's option text: `2.2.a — {first 74 chars}…`.
 *
 * The ellipsis appears only when something was actually cut. §14 writes the label with one
 * because its own example is long; a 30-character article rendered `…` would claim there is
 * more wording than there is, and the preview box beneath — which shows the whole quotation —
 * would then contradict the option the reader just chose.
 */
export function codeOfConductOptionLabel(article, articleText) {
    const collapsed = articleText.replace(/\s+/g, " ").trim();
    const shown = collapsed.length > ARTICLE_OPTION_TEXT_LIMIT
        ? `${collapsed.slice(0, ARTICLE_OPTION_TEXT_LIMIT)}…`
        : collapsed;
    return `${article} — ${shown}`;
}
//# sourceMappingURL=code-of-conduct.js.map